'use client';

import {
  getIsLinkExternal,
  getLinkElement,
  getPotentialLinkMailtoHref,
} from '@/lib/link-config';

import { passButtonIcons } from '@/components/atoms/Button/utils';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { SvgIcon } from '@/components/SvgIcon';

import HomepageLinkButton, {
  HomepageLinkButtonStrapi,
} from '@/features/HomepageHero/components/HomepageLinkButton';
import { getIsGoogleButton } from '@/features/HomepageHero/helpers';
import { passPhTracker } from '@/integrations/trackingScripts/utils';

import iconGoogleLetterSrc from '~/svg/IconGoogleLetter.svg';

type ExtraButtonType = 'googleSignIn';

function ExtraButton({
  link,
  text,
  phTracker,
  eventName,
}: {
  link: string;
  text: string;
  phTracker?: string;
  eventName?: string;
}) {
  return (
    <HomepageLinkButton
      eventName={eventName}
      button={{
        variant: 'outlined',
        color: 'secondary',
        as: getLinkElement(link),
        href: getPotentialLinkMailtoHref(link),
        target: getIsLinkExternal(link) ? '_blank' : undefined,
        children: text,
        classNames: { root: 'w-full md:w-auto' },
        ...passPhTracker(phTracker),
        ...passButtonIcons({
          fallbackIcon: {
            leftSection: <SvgIcon src={iconGoogleLetterSrc} asImg />,
          },
        }),
      }}
    />
  );
}

function modifyFirstLinkProps(index: number) {
  if (index === 0) {
    return {
      variant: 'filled' as const,
      color: 'primary' as const,
    };
  }

  return {
    variant: 'outlined' as const,
    color: 'secondary' as const,
  };
}

function HeroLinkButton({
  link,
  index,
  eventName,
}: {
  link: LinkButtonStrapiProps;
  index: number;
  eventName?: string;
}) {
  const isGoogleButton = getIsGoogleButton(link.url);

  return (
    <HomepageLinkButtonStrapi
      block={link}
      eventName={eventName}
      inner={{
        classNames: { root: 'w-full md:w-auto' },
        ...passButtonIcons(link),
        variant: link.variant || modifyFirstLinkProps(index).variant,
        color: link.color || modifyFirstLinkProps(index).color,
        leftSection: isGoogleButton ? (
          <SvgIcon src={iconGoogleLetterSrc} asImg />
        ) : undefined,
      }}
    />
  );
}

export function HeroLinks({
  links,
  extraButton,
  extraButtonPhTracker,
  extraButtonLink,
  extraButtonText,
  eventName = 'hero_cta_click',
}: {
  links: LinkButtonStrapiProps[];
  extraButton?: ExtraButtonType;
  extraButtonLink?: string;
  extraButtonText?: string;
  extraButtonPhTracker?: string;
  eventName?: string;
}) {
  if (!links?.length) return null;

  return (
    <>
      {links.map((link, index) => (
        <HeroLinkButton
          key={link.url}
          link={link}
          index={index}
          eventName={eventName}
        />
      ))}

      {extraButton && extraButtonLink && extraButtonText && (
        <ExtraButton
          link={extraButtonLink}
          text={extraButtonText}
          phTracker={extraButtonPhTracker}
          eventName={eventName}
        />
      )}
    </>
  );
}
